import {
  type AIPlanDto,
  type AliasProjection,
  type AttachmentProjection,
  type BouncedEmailDto,
  type BouncedRecipientDto,
  type Complaint,
  type ConnectorEventProjection,
  ConnectorEventDtoStatusEnum,
  ConnectorEventProjectionStatusEnum,
  type ConnectorPlanDto,
  CreateInboxForwarderOptionsFieldEnum,
  CreateInboxReplierOptionsFieldEnum,
  type DeliveryStatusDto as DeliveryStatus,
  type DomainPreview,
  type EmailPlanDto,
  type EmailProjection,
  type EmailValidationRequestDto as EmailValidationRequest,
  type ExpiredInboxRecordProjection,
  type InboxForwarderDto,
  type InboxForwarderEventProjection,
  InboxForwarderEventProjectionStatusEnum,
  type InboxPlanDto,
  type InboxPreview as InboxProjection,
  type InboxReplierDto,
  type InboxReplierEventProjection,
  InboxReplierEventProjectionStatusEnum,
  type InboxRulesetDto,
  type InvoiceDto,
  type MissedEmailProjection,
  type NotificationProjection,
  type OrganizationUserProjection,
  type PhoneNumberProjection,
  type PhonePlanDto,
  type QuoteDto,
  type ScheduledJobDto,
  ScheduledJobDtoStatusEnum,
  type SentEmailProjection,
  type SmsProjection,
  type TemplateProjection,
  type ThreadProjection,
  type TrackingPixelProjection,
  type User,
  type UserPlanDto,
  type WebhookDto,
  WebhookDtoEventNameEnum,
  type WebhookResultDto,
  WebhookResultDtoResultTypeEnum,
} from '@mailslurp/admin-sdk';
import { type Field } from './helpers';
import { Variant } from '~/store/_types';
import { assertUnreachable } from './assertUnreachable';

export const pageAIPlan: Field<AIPlanDto>[] = [
  { key: 'id', label: 'ID', uuid: true },
  { key: 'createdAt', label: 'Received', ago: true, sortable: true },
];
export const pageConnectorPlan: Field<ConnectorPlanDto>[] = [
  { key: 'id', label: 'ID', uuid: true },
  { key: 'createdAt', label: 'Received', ago: true, sortable: true },
];
export const pageInboxPlan: Field<InboxPlanDto>[] = [
  { key: 'id', label: 'ID', uuid: true },
  { key: 'createdAt', label: 'Received', ago: true, sortable: true },
];
export const pageEmailPlan: Field<EmailPlanDto>[] = [
  { key: 'id', label: 'ID', uuid: true },
  { key: 'createdAt', label: 'Received', ago: true, sortable: true },
];
export const pageUserPlan: Field<UserPlanDto>[] = [
  { key: 'id', label: 'ID', uuid: true },
  { key: 'seats', label: 'Seats' },
  { key: 'createdAt', label: 'Received', ago: true, sortable: true },
];
export const pagePhonePlan: Field<PhonePlanDto>[] = [
  { key: 'id', label: 'ID', uuid: true },
  { key: 'phoneCountry', label: 'Region', phoneCountry: true },
  { key: 'createdAt', label: 'Received', ago: true, sortable: true },
];
export const pageVerificationRequest: Field<EmailValidationRequest>[] = [
  { key: 'id', label: 'ID', uuid: true, supplyTo: (t) => `/emails/${t.id}` },
  { key: 'emailAddress', label: 'Email address' },
  {
    key: 'isValid',
    label: 'Valid',
    badgeVariant: (t) => (t.isValid ? Variant.success : Variant.warning),
    badge: true,
    supply: (t) => (t.isValid ? 'Valid' : 'Invalid'),
  },
  { key: 'createdAt', label: 'Created', ago: true, sortable: true },
];
export const pageEmailProjectionWithSlot: Field<EmailProjection>[] = [
  { key: 'createdAt', label: 'Received', ago: true, sortable: true },
];
export const pageEmailProjection: Field<EmailProjection>[] = [
  { key: 'id', label: 'ID', uuid: true, supplyTo: (t) => `/emails/${t.id}` },
  {
    key: 'inboxId',
    label: 'Inbox',
    uuid: true,
    supplyTo: (t: EmailProjection) => `/inboxes/${t.inboxId}`,
  },
  { key: 'from', label: 'From' },
  { key: 'subject', label: 'Subject' },
  { key: 'bodyExcerpt', label: 'Body', noHtml: true },
  { label: ' ', attachmentCount: true },
  { key: 'createdAt', label: 'Received', ago: true, sortable: true },
];

export const pageSms: Field<SmsProjection>[] = [
  {
    key: 'id',
    label: 'ID',
    uuid: true,
    supplyTo: (t) => `/phone-numbers/${t.phoneNumber}/sms/${t.id}`,
  },
  { key: 'fromNumber', label: 'From' },
  { key: 'phoneNumber', label: 'To' },
  { key: 'createdAt', label: 'Received', ago: true, sortable: true },
];

export const pagePhoneNumber: Field<PhoneNumberProjection>[] = [
  {
    key: 'id',
    label: 'ID',
    uuid: true,
    supplyTo: (t) => `/phone-numbers/${t.id}`,
  },
  { key: 'phoneNumber', label: 'Number' },
  { key: 'phoneCountry', label: 'Region', phoneCountry: true },
  { key: 'createdAt', label: 'Received', ago: true, sortable: true },
];

export const pageExpiredInboxRecordProjection: Field<ExpiredInboxRecordProjection>[] =
  [
    {
      key: 'id',
      label: 'ID',
      uuid: true,
      dataId: 'expired-table-id',
      supplyTo: (i: ExpiredInboxRecordProjection) => `/inboxes/${i.id}`,
    },
    {
      key: 'emailAddress',
      dataId: 'expired-table-email',
      label: 'Email address',
      supply: (i: ExpiredInboxRecordProjection) => i.emailAddress,
    },
    { key: 'createdAt', label: 'Created', ago: true, sortable: true },
  ];
export const pageNotificationProjection: Field<NotificationProjection>[] = [
  {
    key: 'id',
    label: 'ID',
    uuid: true,
    supplyTo: (t) => `/notifications/${t.id}`,
  },
  { key: 'notificationType', label: 'Type' },
  { key: 'subject', label: 'Subject' },
  { key: 'createdAt', label: 'Created', ago: true, sortable: true },
];

export const pageAliasProjection: Field<AliasProjection>[] = [
  {
    key: 'id',
    label: 'ID',
    uuid: true,
    supplyTo(item) {
      return `/aliases/${item.id}`;
    },
  },
  { key: 'emailAddress', label: 'Masked email address' },
  { key: 'createdAt', label: 'Created', ago: true },
];

export const pageAttachmentProjection: Field<AttachmentProjection>[] = [
  {
    key: 'attachmentId',
    label: 'ID',
    supplyTo(t: AttachmentProjection) {
      return `/attachments/${t.attachmentId}`;
    },
    uuid: true,
  },
  { key: 'name', label: 'File name' },
  { key: 'contentType', label: 'Content type' },
  { key: 'contentLength', label: 'Size', byteCount: true },
  { key: 'createdAt', label: 'Created', ago: true, sortable: true },
];
export const pageBouncedRecipientProjection: Field<BouncedRecipientDto>[] = [
  {
    key: 'id',
    label: 'ID',
    uuid: true,
    supplyTo(t: BouncedRecipientDto) {
      return `/reputation/recipients/${t.id}`;
    },
  },
  { key: 'recipient', label: 'Email address' },
  { key: 'action', label: 'Action' },
  { key: 'status', label: 'Status' },
  { key: 'createdAt', label: 'Created', ago: true, sortable: true },
];
export const pageBouncedEmailProjection: Field<BouncedEmailDto>[] = [
  {
    key: 'id',
    label: 'ID',
    uuid: true,
    supplyTo(t: BouncedEmailDto) {
      return `/reputation/emails/${t.id}`;
    },
  },
  { key: 'bounceType', label: 'Type' },
  { key: 'subject', label: 'Subject' },
  { key: 'sender', label: 'Sender' },
  { key: 'sentToRecipients', label: 'Recipients' },
  { key: 'createdAt', label: 'Created', ago: true, sortable: true },
];
export const pageMissedEmailProjection: Field<MissedEmailProjection>[] = [
  {
    key: 'id',
    label: 'ID',
    uuid: true,
    supplyTo(t: MissedEmailProjection) {
      return `/missed-emails/${t.id}`;
    },
  },
  { key: 'subject', label: 'Subject' },
  { key: 'createdAt', label: 'Created', ago: true, sortable: true },
];
export const pageInboxProjection: Field<InboxProjection>[] = [
  {
    key: 'id',
    label: 'ID',
    uuid: true,
    dataId: 'inboxes-table-id',
    supplyTo: (t: InboxProjection) => `/inboxes/${t.id}`,
  },
  {
    key: 'emailAddress',
    dataId: 'inboxes-table-email',
    label: 'Email address',
    inboxEmailAddress: true,
    supplyTo: (t: InboxProjection) => `/inboxes/${t.id}`,
  },
  {
    key: 'name',
    label: 'Name',
    dataId: 'inboxes-table-name',
  },
  { key: 'expiresAt', label: 'Expires', expiresAt: true },
  { key: 'createdAt', label: 'Created', ago: true, sortable: true },
];
export const pageTemplateProjection: Field<TemplateProjection>[] = [
  {
    key: 'id',
    label: 'ID',
    uuid: true,
    supplyTo: (t) => `/templates/${t.id}`,
  },
  { key: 'name', label: 'Name' },
  { key: 'variables', label: 'Variables' },
  { key: 'createdAt', label: 'Created', ago: true },
];
export const pageOrgUsersPagination: Field<OrganizationUserProjection>[] = [
  {
    key: 'id',
    label: 'ID',
    uuid: true,
  },
  { key: 'name', label: 'Name' },
  { key: 'emailAddress', label: 'Email' },
  { key: 'userType', label: 'Permissions' },
  { key: 'verified', label: 'Status' },
  { key: 'createdAt', ago: true, label: 'Created', sortable: true },
];
export const pageOrgUsers: Field<User>[] = [
  {
    key: 'id',
    label: 'ID',
    uuid: true,
  },
  { key: 'name', label: 'Name' },
  { key: 'emailAddress', label: 'Email' },
  { key: 'userType', label: 'Permissions' },
  { key: 'verified', label: 'Status' },
  { key: 'createdAt', ago: true, label: 'Created', sortable: true },
];

export const pageInboxForwarderProjection: Field<InboxForwarderDto>[] = [
  {
    key: 'id',
    label: 'ID',
    uuid: true,
    supplyTo(t: InboxForwarderDto) {
      return `/forwarders/${t.id}`;
    },
  },
  {
    key: 'field',
    label: 'Field',
  },
  {
    key: 'match',
    label: 'Match',
  },
  {
    key: 'forwardToRecipients',
    label: 'Recipients',
  },
  { key: 'createdAt', label: 'Created', ago: true, sortable: true },
];
export const pageInboxRulesetsProjection: Field<InboxRulesetDto>[] = [
  {
    key: 'id',
    label: 'ID',
    uuid: true,
    supplyTo: (t: InboxRulesetDto) => `/inbox-rulesets/${t.id}`,
  },
  {
    key: 'inboxId',
    label: 'Inbox',
    uuid: true,
    supplyTo: (t: InboxRulesetDto) => `/inboxes/${t.inboxId}`,
  },
  { key: 'scope', label: 'Scope' },
  { key: 'action', label: 'Action' },
  { key: 'target', label: 'Target' },
  { key: 'createdAt', label: 'Created', ago: true, sortable: true },
];
export const pageSentEmailProjection: Field<SentEmailProjection>[] = [
  {
    key: 'id',
    label: 'ID',
    uuid: true,
    supplyTo(t: SentEmailProjection) {
      return `/sent/${t.id}`;
    },
  },
  { key: 'subject', label: 'Subject' },
  { key: 'createdAt', label: 'Sent', ago: true, sortable: true },
];
export const pageValidationRequest: Field<EmailValidationRequest>[] = [
  {
    key: 'id',
    label: 'ID',
    uuid: true,
  },
  {
    key: 'isValid',
    label: 'Is valid',
    supply: (t) => (t.isValid ? 'VALID' : 'INVALID'),
  },
  { key: 'emailAddress', label: 'Email address' },
  { key: 'createdAt', label: 'Created', ago: true, sortable: true },
];
export const pageTrackingPixelProjection: Field<TrackingPixelProjection>[] = [
  {
    key: 'id',
    label: 'ID',
    uuid: true,
    supplyTo: (t: TrackingPixelProjection) => `/tracking/${t.id}`,
  },
  { key: 'recipient', label: 'Recipient' },
  { key: 'seen', label: 'Seen' },
  { key: 'createdAt', label: 'Created', ago: true, sortable: true },
];
export const pageInboxReplierEvents: Field<InboxReplierEventProjection>[] = [
  {
    label: 'ID',
    key: 'id',
    uuid: true,
    supplyTo(t) {
      return `/repliers/${t.id}/`;
    },
  },
  {
    label: 'Recipients',
    key: 'recipients',
  },
  {
    label: 'Status',
    key: 'status',
    badge: true,
    badgeVariant(t) {
      return t.status === InboxReplierEventProjectionStatusEnum.FAILURE
        ? Variant.danger
        : Variant.success;
    },
  },
  { key: 'createdAt', ago: true, label: 'Created At', sortable: true },
];
export const pageInboxReplier: Field<InboxReplierDto>[] = [
  {
    label: 'ID',
    key: 'id',
    uuid: true,
    supplyTo(t) {
      return `/repliers/${t.id}/`;
    },
  },
  {
    label: 'Name',
    key: 'name',
  },
  {
    label: 'Inbox ID',
    key: 'inboxId',
    uuid: true,
    supplyTo(t) {
      return `/inboxes/${t.inboxId}`;
    },
  },
  { key: 'createdAt', ago: true, label: 'Created At', sortable: true },
];
export const pageWebhookResultEntity: Field<WebhookResultDto>[] = [
  {
    key: 'id',
    label: 'ID',
    uuid: true,
    supplyTo(t) {
      return `/webhooks/${t.webhookId}/results/${t.id}`;
    },
  },
  {
    key: 'inboxId',
    label: 'Inbox',
    uuid: true,
    supplyTo(t) {
      return `/inboxes/${t.inboxId}`;
    },
  },
  {
    key: 'webhookId',
    label: 'Webhook',
    uuid: true,
    supplyTo(t) {
      return `/webhooks/${t.webhookId}`;
    },
  },
  {
    key: 'webhookEvent',
    label: 'Event',
  },
  {
    key: 'responseStatus',
    label: 'Response',
    badge: true,
    badgeVariant: (s) =>
      (s.responseStatus ?? 500) >= 400 ? Variant.danger : Variant.success,
  },
  {
    key: 'resultType',
    label: 'Status',
    badge: true,
    badgeVariant: (s) => {
      if (s.resultType === WebhookResultDtoResultTypeEnum.SUCCESS) {
        return Variant.success;
      } else if (
        s.resultType === WebhookResultDtoResultTypeEnum.EXCEPTION ||
        s.resultType === WebhookResultDtoResultTypeEnum.BAD_RESPONSE
      ) {
        return Variant.danger;
      } else {
        return Variant.info;
      }
    },
  },
  { key: 'createdAt', label: 'Created', ago: true, sortable: true },
];
export const pageAllInboxForwarderEvents: Field<InboxForwarderEventProjection>[] =
  [
    {
      key: 'id',
      label: 'ID',
    },
    {
      key: 'forwarderId',
      label: 'Forwarder ID',
      uuid: true,
      supplyTo(t) {
        return `/forwarders/${t.emailId}`;
      },
    },
    {
      key: 'emailId',
      label: 'Email ID',
      uuid: true,
      supplyTo(t) {
        return `/emails/${t.emailId}`;
      },
    },
    {
      key: 'status',
      label: 'Status',
      badge: true,
      supply(t) {
        return t.status ?? 'PENDING';
      },
      badgeVariant(t) {
        switch (t.status) {
          case InboxForwarderEventProjectionStatusEnum.SUCCESS:
            return Variant.success;
          case InboxForwarderEventProjectionStatusEnum.FAILURE:
            return Variant.danger;
          case undefined:
            return Variant.info;
          default:
            assertUnreachable(t.status);
        }
      },
    },
    { key: 'createdAt', label: 'Created', ago: true, sortable: true },
  ];

export function connectorEventStatusVariant(
  status?: ConnectorEventProjectionStatusEnum | ConnectorEventDtoStatusEnum,
) {
  switch (status) {
    case ConnectorEventDtoStatusEnum.SUCCESS:
    case ConnectorEventProjectionStatusEnum.SUCCESS:
      return Variant.success;
    case ConnectorEventDtoStatusEnum.CONNECTION_ERROR:
    case ConnectorEventProjectionStatusEnum.CONNECTION_ERROR:
      return Variant.warning;
    case ConnectorEventDtoStatusEnum.SUBSCRIPTION_ERROR:
    case ConnectorEventProjectionStatusEnum.SUBSCRIPTION_ERROR:
      return Variant.danger;
    case ConnectorEventDtoStatusEnum.INTERNAL_ERROR:
    case ConnectorEventProjectionStatusEnum.INTERNAL_ERROR:
      return Variant.danger;
    case ConnectorEventDtoStatusEnum.NOT_FOUND:
    case ConnectorEventProjectionStatusEnum.NOT_FOUND:
      return Variant.danger;
    case undefined:
      return Variant.info;
    default:
      assertUnreachable(status);
  }
}

export const pageConnectorEvents: Field<ConnectorEventProjection>[] = [
  {
    key: 'id',
    label: 'ID',
  },
  {
    key: 'connectorId',
    label: 'Connector ID',
    uuid: true,
    supplyTo(t) {
      return `/connectors/${t.connectorId}`;
    },
  },
  {
    key: 'eventType',
    label: 'Event',
    badge: true,
    supply(t) {
      return t.eventType;
    },
    badgeVariant(t) {
      switch (t.eventType) {
        default:
          return Variant.info;
      }
    },
  },
  {
    key: 'status',
    label: 'Status',
    badge: true,
    supply(t) {
      return t.status ?? 'PENDING';
    },
    badgeVariant(t) {
      return connectorEventStatusVariant(t.status);
    },
  },
  { key: 'createdAt', label: 'Created', ago: true, sortable: true },
];
export const pageInboxForwarderEvents: Field<InboxForwarderEventProjection>[] =
  [
    {
      key: 'id',
      label: 'ID',
    },
    {
      key: 'forwarderId',
      label: 'Forwarder ID',
      uuid: true,
      supplyTo(t) {
        return `/forwarders/${t.emailId}`;
      },
    },
    {
      key: 'emailId',
      label: 'Email ID',
      uuid: true,
      supplyTo(t) {
        return `/emails/${t.emailId}`;
      },
    },
    {
      key: 'status',
      label: 'Status',
      badge: true,
      supply(t) {
        return t.status ?? 'PENDING';
      },
      badgeVariant(t) {
        switch (t.status) {
          case InboxForwarderEventProjectionStatusEnum.SUCCESS:
            return Variant.success;
          case InboxForwarderEventProjectionStatusEnum.FAILURE:
            return Variant.danger;
          case undefined:
            return Variant.info;
          default:
            assertUnreachable(t.status);
        }
      },
    },
    { key: 'createdAt', label: 'Created', ago: true, sortable: true },
  ];
export const pageScheduledJobs: Field<ScheduledJobDto>[] = [
  {
    key: 'id',
    label: 'ID',
    uuid: true,
    supplyTo(t: ScheduledJobDto) {
      return `/scheduled/${t.id}`;
    },
  },
  {
    key: 'inboxId',
    label: 'Inbox ID',
    uuid: true,
    supplyTo(t: ScheduledJobDto) {
      return `/inboxes/${t.inboxId}`;
    },
  },
  {
    key: 'status',
    label: 'Status',
    badge: true,
    badgeVariant: (t) => {
      switch (t.status) {
        case ScheduledJobDtoStatusEnum.SUBMITTED:
          return Variant.info;
        case ScheduledJobDtoStatusEnum.COMPLETED:
          return Variant.success;
        case ScheduledJobDtoStatusEnum.FAILED:
          return Variant.danger;
        case ScheduledJobDtoStatusEnum.CANCELLED:
          return Variant.warning;
        case ScheduledJobDtoStatusEnum.ABORTED:
          return Variant.warning;
        default:
          assertUnreachable(t.status);
      }
    },
  },
  { key: 'createdAt', label: 'Created', ago: true, sortable: true },
];

export const pageDeliveryStatus: Field<DeliveryStatus>[] = [
  {
    key: 'id',
    label: 'ID',
    uuid: true,
    supplyTo(t: DeliveryStatus) {
      return `/sent/delivery/${t.id}`;
    },
  },
  {
    key: 'sentId',
    label: 'Sent ID',
    uuid: true,
    supplyTo(t: DeliveryStatus) {
      return `/sent/${t.sentId}`;
    },
  },
  {
    key: 'inboxId',
    label: 'Inbox ID',
    uuid: true,
    supplyTo(t: DeliveryStatus) {
      return `/inboxes/${t.inboxId}`;
    },
  },
  {
    key: 'smtpStatusCode',
    label: 'Status',
    badge: true,
    badgeVariant: (t) => {
      const code = t.smtpStatusCode ?? 500;
      if (code < 300) {
        return Variant.success;
      }
      if (code < 400) {
        return Variant.warning;
      }
      if (code >= 400) {
        return Variant.danger;
      }
      return Variant.success;
    },
  },
  {
    key: 'subject',
    label: 'Subject',
  },
  {
    key: 'recipients',
    label: 'Recipients',
  },
  { key: 'createdAt', label: 'Created', ago: true, sortable: true },
];
export const pageComplaint: Field<Complaint>[] = [
  {
    key: 'id',
    label: 'ID',
    uuid: true,
    supplyTo(t: Complaint) {
      return `/complaints/${t.id}`;
    },
  },
  {
    key: 'complaintRecipient',
    label: 'Complainant',
  },
  {
    key: 'mailSource',
    label: 'Source',
  },
  { key: 'createdAt', label: 'Created', ago: true, sortable: true },
];
export const pageThreadPagination: Field<ThreadProjection>[] = [
  {
    key: 'id',
    label: 'ID',
    uuid: true,
  },
  {
    key: 'inboxId',
    label: 'Inbox',
    uuid: true,
    supplyTo(t: ThreadProjection) {
      return `/inboxes/${t.inboxId}`;
    },
  },
  {
    key: 'to',
    label: 'To',
    supply(t: ThreadProjection) {
      return t.to?.join(',') ?? '-';
    },
  },
  { key: 'createdAt', label: 'Created', ago: true, sortable: true },
];
export const pageWebhookProjection: Field<WebhookDto>[] = [
  {
    key: 'id',
    label: 'ID',
    uuid: true,
    supplyTo(t: WebhookDto) {
      return `/webhooks/${t.id}`;
    },
  },
  {
    key: 'name',
    label: 'Name',
  },
  {
    key: 'inboxId',
    label: 'Inbox',
    uuid: true,
    supplyTo(t: WebhookDto) {
      return `/inboxes/${t.inboxId}`;
    },
  },
  {
    key: 'eventName',
    label: 'Event',
    supply(t: WebhookDto) {
      return t.eventName ? t.eventName : WebhookDtoEventNameEnum.EMAIL_RECEIVED;
    },
  },
  { key: 'url', label: 'Endpoint URL' },
  { key: 'createdAt', label: 'Created', ago: true, sortable: true },
];

export const listQuotes: Field<QuoteDto>[] = [
  { key: 'id', label: 'ID' },
  { key: 'quoteStatus', label: 'Status' },
];
export const listInvoices: Field<InvoiceDto>[] = [
  { key: 'id', label: 'ID' },
  {
    key: 'periodStart',
    label: 'Period start',
  },
  {
    key: 'periodEnd',
    label: 'Period end',
  },
  {
    key: 'status',
    label: 'Status',

    supply: (i) => i.status?.toUpperCase() ?? '',
    badge: true,
    badgeVariant: (s) => {
      const status = s.status;
      switch (status) {
        case 'draft':
          return Variant.info;
        case 'open':
          return Variant.warning;
        case 'paid':
          return Variant.primary;
        case 'uncollectible':
          return Variant.danger;
        case 'void':
          return Variant.warning;
        default:
          return undefined;
      }
    },
  },
  { key: 'date', label: 'Date', ago: true },
];

export const pageDomainProjection: Field<DomainPreview>[] = [
  {
    key: 'id',
    uuid: true,
    label: 'ID',
    supplyTo: (t: DomainPreview) => `/domains/${t.id}`,
  },
  { key: 'domain', label: 'Domain' },
  {
    key: 'isVerified',
    label: 'Verified',
    badge: true,
    supply: (d) => (d.isVerified ? 'Verified' : 'Not Verified'),
    badgeVariant: (d) => (d.isVerified ? Variant.primary : Variant.warning),
  },
  {
    key: 'hasMissingRecords',
    label: 'Records',
    badge: true,
    supply: (d) => (d.hasMissingRecords ? 'Missing' : 'Present'),
    badgeVariant: (d) =>
      d.hasMissingRecords ? Variant.danger : Variant.primary,
  },
  { key: 'domainType', label: 'Type' },
  { key: 'createdAt', label: 'Created', ago: true },
];
export const inboxReplierFields = [
  CreateInboxReplierOptionsFieldEnum.SUBJECT,
  CreateInboxReplierOptionsFieldEnum.SENDER,
  CreateInboxReplierOptionsFieldEnum.RECIPIENTS,
  CreateInboxReplierOptionsFieldEnum.ATTACHMENTS,
].map((it) => ({ text: it, value: it }));
export const inboxForwarderFields = [
  CreateInboxForwarderOptionsFieldEnum.SUBJECT,
  CreateInboxForwarderOptionsFieldEnum.SENDER,
  CreateInboxForwarderOptionsFieldEnum.RECIPIENTS,
  CreateInboxForwarderOptionsFieldEnum.ATTACHMENTS,
].map((it) => ({ text: it, value: it }));
